/**
 * LottieLoader Module
 *
 * Lazy-loads Lottie animations when their container enters the
 * viewport.  Dynamically injects the lottie-player library if it
 * has not been loaded yet, then plays/pauses animations based on
 * visibility.
 *
 * Expected markup:
 *   <div data-lottie-src="/path/to/animation.json"
 *        data-lottie-loop="true"
 *        data-lottie-autoplay="true">
 *   </div>
 *
 * @module LottieLoader
 */
export default class LottieLoader {

  static DEFAULTS = {
    selector: '[data-lottie-src]',
    threshold: 0.25,
    rootMargin: '0px',
    playerSrc: 'https://unpkg.com/@lottiefiles/lottie-player@2/dist/lottie-player.js',
  };

  /**
   * @param {Object} options - Merged with DEFAULTS.
   */
  constructor(options = {}) {
    this.config = { ...LottieLoader.DEFAULTS, ...options };

    this.containers = document.querySelectorAll(this.config.selector);
    if (!this.containers.length) {
      return;
    }

    this._playerLoaded = this._isPlayerAvailable();
    this._playerLoadPromise = null;
    this._observer = null;
    this._visibilityObserver = null;
    this._players = new Map(); // container -> lottie-player element

    this._init();
  }

  /* ------------------------------------------------------------------
   * Bootstrap
   * ----------------------------------------------------------------*/

  _init() {
    this._initState();
    this._createLoadObserver();
    this._createVisibilityObserver();
  }

  _initState() {
    this.containers.forEach((container) => {
      container.setAttribute('data-state', 'idle');
    });
  }

  /* ------------------------------------------------------------------
   * lottie-player script injection
   * ----------------------------------------------------------------*/

  _isPlayerAvailable() {
    return typeof customElements !== 'undefined'
      && customElements.get('lottie-player') !== undefined;
  }

  /**
   * Load the lottie-player script once. Returns a shared promise so
   * concurrent calls do not inject the script multiple times.
   *
   * @return {Promise<void>}
   */
  _ensurePlayer() {
    if (this._playerLoaded) {
      return Promise.resolve();
    }

    if (this._playerLoadPromise) {
      return this._playerLoadPromise;
    }

    this._playerLoadPromise = new Promise((resolve, reject) => {
      // Double-check in case something else loaded it in the meantime
      if (this._isPlayerAvailable()) {
        this._playerLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = this.config.playerSrc;
      script.async = true;

      script.addEventListener('load', () => {
        this._playerLoaded = true;
        resolve();
      });

      script.addEventListener('error', () => {
        reject(new Error(`Failed to load lottie-player from ${this.config.playerSrc}`));
      });

      document.head.appendChild(script);
    });

    return this._playerLoadPromise;
  }

  /* ------------------------------------------------------------------
   * Lazy-load observer (load animation when entering viewport)
   * ----------------------------------------------------------------*/

  _createLoadObserver() {
    this._observer = new IntersectionObserver(
      this._handleLoadIntersection.bind(this),
      {
        threshold: this.config.threshold,
        rootMargin: this.config.rootMargin,
      },
    );

    this.containers.forEach((container) => this._observer.observe(container));
  }

  async _handleLoadIntersection(entries) {
    for (const entry of entries) {
      if (!entry.isIntersecting) {
        continue;
      }

      const container = entry.target;

      // Only load once
      if (container.getAttribute('data-state') !== 'idle') {
        continue;
      }

      container.setAttribute('data-state', 'loading');
      this._observer.unobserve(container);

      try {
        await this._ensurePlayer();
        this._createPlayer(container);
        container.setAttribute('data-state', 'loaded');

        // Start observing for play/pause
        this._visibilityObserver.observe(container);
      } catch (error) {
        container.setAttribute('data-state', 'error');
        console.error('[LottieLoader]', error.message, container);
      }
    }
  }

  /* ------------------------------------------------------------------
   * Player creation
   * ----------------------------------------------------------------*/

  _createPlayer(container) {
    const src = container.getAttribute('data-lottie-src');
    if (!src) {
      return;
    }

    const player = document.createElement('lottie-player');
    player.setAttribute('src', src);
    player.setAttribute('background', 'transparent');
    player.style.width = '100%';
    player.style.height = '100%';

    // Optional attributes
    if (container.getAttribute('data-lottie-loop') !== 'false') {
      player.setAttribute('loop', '');
    }

    if (container.getAttribute('data-lottie-autoplay') !== 'false') {
      player.setAttribute('autoplay', '');
    }

    const speed = container.getAttribute('data-lottie-speed');
    if (speed) {
      player.setAttribute('speed', speed);
    }

    const mode = container.getAttribute('data-lottie-mode');
    if (mode) {
      player.setAttribute('mode', mode);
    }

    container.appendChild(player);
    this._players.set(container, player);
  }

  /* ------------------------------------------------------------------
   * Visibility observer (play / pause)
   * ----------------------------------------------------------------*/

  _createVisibilityObserver() {
    this._visibilityObserver = new IntersectionObserver(
      this._handleVisibility.bind(this),
      { threshold: 0.1 },
    );
  }

  _handleVisibility(entries) {
    entries.forEach((entry) => {
      const player = this._players.get(entry.target);
      if (!player) {
        return;
      }

      // lottie-player exposes play/pause via its API.  Ensure the
      // custom element is fully upgraded before calling methods.
      if (typeof player.play !== 'function') {
        return;
      }

      if (entry.isIntersecting) {
        player.play();
        entry.target.setAttribute('data-state', 'playing');
      } else {
        player.pause();
        entry.target.setAttribute('data-state', 'paused');
      }
    });
  }

  /* ------------------------------------------------------------------
   * Public API
   * ----------------------------------------------------------------*/

  /** Tear down observers and remove injected players. */
  destroy() {
    if (this._observer) {
      this._observer.disconnect();
    }
    if (this._visibilityObserver) {
      this._visibilityObserver.disconnect();
    }

    this._players.forEach((player, container) => {
      if (typeof player.stop === 'function') {
        player.stop();
      }
      player.remove();
      container.setAttribute('data-state', 'idle');
    });

    this._players.clear();
  }
}
